import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DollarSign, ShoppingCart, TrendingUp, Users, Package, AlertTriangle, Truck, Wallet } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { money, StatusBadge } from "@/components/admin/AdminLayout";

const KPI = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="bg-white border border-zinc-200 rounded-xl p-5 hover:border-zinc-300 transition-colors" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center justify-between">
      <span className="text-xs text-zinc-500 font-medium">{label}</span>
      <div className={`p-1.5 rounded-lg ${accent || "bg-zinc-100 text-zinc-700"}`}><Icon size={15} /></div>
    </div>
    <p className="text-2xl font-bold mt-3 font-display tracking-tight">{value}</p>
    {sub && <p className="text-xs text-zinc-400 mt-1">{sub}</p>}
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="animate-pulse space-y-4"><div className="h-24 bg-zinc-100 rounded-xl" /><div className="h-72 bg-zinc-100 rounded-xl" /></div>;

  const k = data.kpis;
  const s = data.order_status;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-sm text-zinc-500">Store performance at a glance</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={DollarSign} label="Total Revenue" value={money(k.total_revenue)} accent="bg-blue-100 text-blue-700" />
        <KPI icon={ShoppingCart} label="Total Orders" value={k.total_orders} accent="bg-indigo-100 text-indigo-700" />
        <KPI icon={Wallet} label="Supplier Costs" value={money(k.supplier_costs)} accent="bg-amber-100 text-amber-700" />
        <KPI icon={TrendingUp} label="Net Est. Profit" value={money(k.net_estimated_profit)} sub={`Gross ${money(k.gross_profit)}`} accent="bg-green-100 text-green-700" />
        <KPI icon={Users} label="Customers" value={k.customers} />
        <KPI icon={Package} label="Active Products" value={`${k.active_products}/${k.products}`} />
        <KPI icon={AlertTriangle} label="Out of Stock" value={k.out_of_stock} accent="bg-red-100 text-red-700" />
        <KPI icon={Truck} label="Pending Orders" value={k.pending_orders} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-xl p-5">
          <h3 className="font-semibold text-sm mb-4">Revenue · Last 14 days</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.revenue_series}>
              <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#2563EB" stopOpacity={0.3} /><stop offset="95%" stopColor="#2563EB" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} stroke="#a1a1aa" />
              <YAxis tick={{ fontSize: 10 }} stroke="#a1a1aa" />
              <Tooltip formatter={(v) => money(v)} />
              <Area type="monotone" dataKey="revenue" stroke="#2563EB" strokeWidth={2} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-zinc-200 rounded-xl p-5">
          <h3 className="font-semibold text-sm mb-4">Orders by Status</h3>
          <div className="space-y-2.5">
            {[["Awaiting Fulfillment", s.awaiting_fulfillment], ["Processing", s.processing], ["Shipped", s.shipped],
              ["Delivered", s.delivered], ["Cancelled", s.cancelled], ["Refunded", s.refunded],
              ["Requires Attention", s.requires_attention]].map(([label, val]) => (
              <div key={label} className="flex items-center justify-between text-sm">
                <span className="text-zinc-600">{label}</span>
                <span className="font-semibold">{val}</span>
              </div>
            ))}
          </div>
          {s.requires_attention > 0 && (
            <Link to="/admin/orders?flagged=1" className="block mt-4 bg-red-50 text-red-700 text-xs rounded-lg p-3 font-medium">
              {s.requires_attention} order(s) require attention →
            </Link>
          )}
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl p-5">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-sm">Recent Orders</h3>
          <Link to="/admin/orders" className="text-xs text-blue-600 font-medium">View all</Link>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-zinc-500 border-b border-zinc-100">
            <th className="pb-2 font-medium">Order</th><th className="pb-2 font-medium">Customer</th>
            <th className="pb-2 font-medium">Total</th><th className="pb-2 font-medium">Profit</th><th className="pb-2 font-medium">Status</th>
          </tr></thead>
          <tbody>
            {data.recent_orders.map((o) => (
              <tr key={o.id} className="border-b border-zinc-50">
                <td className="py-2.5"><Link to={`/admin/orders/${o.id}`} className="font-mono text-xs font-medium text-blue-600">{o.order_number}</Link></td>
                <td className="py-2.5 text-zinc-600">{o.shipping_address?.name || o.email}</td>
                <td className="py-2.5 font-medium">{money(o.total)}</td>
                <td className="py-2.5 text-green-600">{money(o.estimated_profit)}</td>
                <td className="py-2.5"><StatusBadge status={o.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
